import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { matchPath, useLocation } from 'react-router-dom'
import { UAParser } from 'ua-parser-js'

import { useDeviceReferral } from '@pages/Referral/DeviceReferral'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useProductReadContext } from '@context/ProductContext'
import { useChineseSessionLanguage } from '@context/SessionSettingsContext'

import { Body1, Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { Link } from '@ui/components/Link'
import { RichIcon } from '@ui/components/RichIcon'
import { CloseIcon } from '@ui/icons/Close.icon'
import { MobileAppIcon } from '@ui/icons/platforms/MobileApp.icon'

import { useWindowResize } from '@utils/domUtils'
import { CommonPaths } from '@utils/path'

import { isAccountActivatedStatus } from '@model/AccountDetailedStatus'
import { OperatingSystemNameEnum } from '@model/PlatformTypeEnum'

import { useLocallyPersistedState, useSessionPersistedState } from '@hooks/useStorage'

import { TickmillAppRatingGraphic } from '@assets/graphics/TickmillAppRating.graphic'

import { Box } from '@shared/components/Box'

import { TickmillAppBannerClosedKey } from './TickmillAppBanner.constants'

// TODO: at some point we should make these all just layout based through routing logic
const excludedPaths = [
  ':locale/sign-up/*',
  ':locale/users',
  `:locale${CommonPaths.TradersRoomWallets}/:id/deposit`,
  `:locale${CommonPaths.TradersRoomWallets}/deposit`,
  `:locale${CommonPaths.TradersRoomWallets}/:id/transfer`,
  `:locale${CommonPaths.TradersRoomWallets}/transfer`,
  `:locale${CommonPaths.IntroducingBrokerWallets}/:id/transfer`,
  `:locale${CommonPaths.PaymentAgentWallets}/:id/transfer`,
  ':locale/dashboard/traders-room/trading-accounts/:id/transfer',
  `:locale${CommonPaths.TradersRoomWallets}/:id/withdrawal`,
  `:locale${CommonPaths.TradersRoomWallets}/withdrawal`,
  `:locale${CommonPaths.IntroducingBrokerWallets}/:id/withdrawal`,
  `:locale${CommonPaths.IntroducingBrokerWallets}/withdrawal`,
  ':locale/dashboard/traders-room/trading-accounts/:id/demo/add-balance',
  `:locale${CommonPaths.NewLiveTradingAccount}`,
  `:locale${CommonPaths.NewDemoTradingAccount}`,
  ':locale/dashboard/traders-room/trading-accounts/:id/change-password',
  ':locale/dashboard/traders-room/transactions/conversion',
  ':locale/dashboard/product-registration/*',
  ':locale/dashboard/update-kyc',
  `:locale${CommonPaths.KycUpdateDetails}`,
  `:locale${CommonPaths.FirstTimeDeposit}`,
  ':locale/first-time-deposit',
]

const loginPaths = [':locale/login', ':locale/forgot-password', ':locale/logout']
const signupPaths = [':locale/sign-up/']

const whitelistPaths = [':locale/sign-up/account-type']

export const TickmillAppBannerDownloadedKey = 'tickmillAppBannerDownloaded'
export { TickmillAppBannerClosedKey } from './TickmillAppBanner.constants'

export const TickmillAppBanner: React.FC = () => {
  const { account } = useAccountReadContext()
  const location = useLocation()
  const isMobile = useWindowResize()
  const { isDefaultCFDProductType } = useProductReadContext()
  const isProductTypeCFD = isDefaultCFDProductType()
  const isChinese = useChineseSessionLanguage()

  const parser = new UAParser()
  const { os } = parser.getResult()
  const isAndroid = os.name === OperatingSystemNameEnum.Android

  const [hasDownloadedApp, setHasDownloadedApp] = useLocallyPersistedState<boolean>(
    TickmillAppBannerDownloadedKey,
    false
  )

  const [hasClosedBanner, setHasClosedBanner] = useLocallyPersistedState<boolean>(
    TickmillAppBannerClosedKey,
    false
  )

  const [isBannerClosedOnLogin, setIsBannerClosedOnLogin] = useSessionPersistedState<boolean>(
    TickmillAppBannerClosedKey,
    false
  )

  const isLoginPage = useMemo(
    () => loginPaths.some((path) => !!matchPath({ path, end: true }, location.pathname)),
    [location.pathname]
  )

  const isSignupPage = useMemo(
    () => signupPaths.some((path) => !!matchPath({ path, end: false }, location.pathname)),
    [location.pathname]
  )

  const handleClose = () => {
    if (isLoginPage) {
      setIsBannerClosedOnLogin(true)
    }
    setHasClosedBanner(true)
  }

  const { referralAppLink } = useDeviceReferral()

  const handleDownload = () => {
    if (isLoginPage) {
      setIsBannerClosedOnLogin(true)
    }
    setHasDownloadedApp(true)
    window.location.href = referralAppLink()
  }

  useEffect(() => {
    if (isLoginPage === undefined) {
      setIsBannerClosedOnLogin(false)
    }
  }, [isLoginPage, location.pathname, setIsBannerClosedOnLogin])

  const shouldShowBanner = () => {
    const isExcluded = excludedPaths.some((pattern) =>
      matchPath({ path: pattern, end: false }, location.pathname)
    )

    const isWhitelisted = whitelistPaths.some((pattern) =>
      matchPath({ path: pattern, end: true }, location.pathname)
    )
    const isExcludedPath = isExcluded && !isWhitelisted
    const hasChinaResidency = account?.addresses.some((x) => x.country.id === 'CHN')
    const isMobileDevice =
      os.name === OperatingSystemNameEnum.Android || os.name === OperatingSystemNameEnum.iOS

    if (hasChinaResidency || !isMobileDevice || isExcludedPath) {
      return false
    }

    if (isSignupPage) {
      return !(hasDownloadedApp || hasClosedBanner) && !(isChinese && isAndroid)
    }

    if (isLoginPage) {
      return !isBannerClosedOnLogin
    }

    if (hasDownloadedApp || hasClosedBanner) {
      return false
    }

    if (account && !isAccountActivatedStatus(account)) {
      return false
    }

    return isProductTypeCFD
  }

  if (!shouldShowBanner() || !isMobile) {
    return null
  }

  return <Banner onClose={handleClose} onDownload={handleDownload} />
}

interface TickmillAppProps {
  onClose: () => void
  onDownload: () => void
}

const Banner: React.FC<TickmillAppProps> = ({ onClose, onDownload }) => {
  const { t } = useTranslation()
  return (
    <Box flex gap='x8' px='x16' py='x8' align='center'>
      <Box flex gap='x8' fullWidth align='center'>
        <ActionIcon icon={{ icon: CloseIcon, color: 'text-med-em' }} onClick={onClose} />
        <RichIcon
          shape='squircle'
          size='x48'
          icon={MobileAppIcon}
          bg='surface-tertiary-low-em'
          border='outline-low-em'
        />
        <Box flex direction='column' fullWidth>
          <Body2 medium>{t('Tickmill App')}</Body2>
          <Box>
            <TickmillAppRatingGraphic />
          </Box>
        </Box>
      </Box>
      <Link onClick={onDownload} underline={false}>
        <Body1>{t('Download')}</Body1>
      </Link>
    </Box>
  )
}
